import { createFileRoute, useLoaderData, useNavigate } from '@tanstack/react-router';
import { useOrganisationMembers } from '~/hooks/useOrganisationMembers';
import { z } from 'zod';
import { useOrganisation } from '~/hooks/useOrganisation';
import { useEffect, useRef, useState } from 'react';
import { Button, Flex, Text } from '@radix-ui/themes';
import { Error as ErrorContainer, ErrorFooter, ErrorMessage, ErrorTitle } from '~/components/errors/Error';
import { ErrorPage } from '~/icons/ErrorPage';
import { useSession } from '~/features/auth/context/Session';
import { OrgApiError, type OrgApiFailure } from '~/utils/errors';
import { LoadingSpinner } from '~/components/LoadingSpinner/LoadingSpinner';

const searchparamsSchema = z.object({
  invite_token: z.string().optional(),
});

export const Route = createFileRoute('/_auth/accept-invitation')({
  component: RouteComponent,
  validateSearch: searchparamsSchema,
  loaderDeps: ({ search }) => ({ search }),
  loader: async ({ deps }) => {
    const { invite_token: inviteToken } = deps.search;
    if (!inviteToken) {
      throw new Error('Missing invite_token in search parameters');
    }

    return {
      inviteToken,
    };
  },
});

type FailureStatus = 'wrongAccount' | 'notFound' | 'alreadyAccepted' | 'expired' | 'memberLimit' | 'error';
type Status = 'pending' | FailureStatus;

function failureStatus(failure: OrgApiFailure): FailureStatus {
  switch (failure.status) {
    case 403:
      return failure.quotaDenied ? 'memberLimit' : 'wrongAccount';
    case 404:
      return 'notFound';
    case 409:
      return 'alreadyAccepted';
    case 410:
      return 'expired';
    default:
      return 'error';
  }
}

const failureContent: Record<FailureStatus, { title: string; message: string; showLogin: boolean }> = {
  wrongAccount: {
    title: 'This invitation is for a different account',
    message:
      'It was sent to a different email address than the one you are logged in with. Log in as the invited user to accept it.',
    showLogin: true,
  },
  notFound: {
    title: 'This invitation link is no longer valid',
    message:
      'It may have been resent, which replaces older links, or cancelled. Check your inbox for a newer invitation, or ask the person who invited you to send it again.',
    showLogin: false,
  },
  alreadyAccepted: {
    title: "You've already accepted this invitation",
    message:
      'There is nothing more to do. If you cannot see the organisation, check that you are logged in as the invited user.',
    showLogin: true,
  },
  expired: {
    title: 'This invitation has expired',
    message: 'Invitations are valid for 7 days. Ask the person who invited you to resend it.',
    showLogin: false,
  },
  memberLimit: {
    title: 'This organisation has reached its member limit',
    message: 'Ask the person who invited you to free up a seat or upgrade their plan, then open this link again.',
    showLogin: false,
  },
  error: {
    title: 'Something went wrong',
    message: 'We were unable to accept your invitation. Please try again or contact the person who invited you.',
    showLogin: true,
  },
};

function RouteComponent() {
  const { inviteToken } = useLoaderData({ from: '/_auth/accept-invitation' });
  const { onUpdateActiveOrganisation } = useOrganisation();
  const { acceptInvitation } = useOrganisationMembers();
  const { handleLogoutAndLogin } = useSession();
  const navigate = useNavigate();

  const [status, setStatus] = useState<Status>('pending');
  const attemptedRef = useRef<string | null>(null);

  useEffect(() => {
    if (!inviteToken) {
      setStatus('error');
      return;
    }

    const attemptKey = inviteToken;
    if (attemptedRef.current === attemptKey) {
      return;
    }
    attemptedRef.current = attemptKey;

    let cancelled = false;

    const handleAcceptInvitation = async () => {
      try {
        const organisationId = await acceptInvitation(inviteToken);
        if (cancelled) return;

        if (!organisationId) {
          throw new Error('Failed to accept invitation');
        }

        await onUpdateActiveOrganisation(organisationId);
      } catch (error) {
        if (cancelled) return;
        console.error('Error accepting invitation:', error);
        setStatus(error instanceof OrgApiError ? failureStatus(error.failure) : 'error');
      }
    };

    void handleAcceptInvitation();

    return () => {
      cancelled = true;
    };
  }, [acceptInvitation, inviteToken, onUpdateActiveOrganisation]);

  if (status === 'pending') {
    return (
      <Flex
        direction="column"
        gap="4"
        justify="center"
        align="center"
        height="100svh"
        width="100svw"
        style={{ backgroundColor: 'var(--gray-2)' }}
      >
        <LoadingSpinner width="5rem" height="5rem" />
        <Text size="3" color="gray">
          Accepting invitation
        </Text>
      </Flex>
    );
  }

  const content = failureContent[status];
  const acceptInvitationUrl = `${window.location.origin}/accept-invitation?invite_token=${encodeURIComponent(inviteToken)}`;

  return (
    <Flex height="100svh" width="100svw" align="center" justify="center" style={{ backgroundColor: 'var(--gray-2)' }}>
      <ErrorContainer>
        <ErrorTitle>{content.title}</ErrorTitle>
        <ErrorMessage>{content.message}</ErrorMessage>
        <ErrorFooter>
          <Button variant="outline" color="gray" onClick={() => void navigate({ to: '/' })}>
            Go to home
          </Button>
          {content.showLogin && (
            <Button
              onClick={() =>
                handleLogoutAndLogin({
                  return_to: acceptInvitationUrl,
                })
              }
            >
              Log in
            </Button>
          )}
        </ErrorFooter>
        <Flex align="center" justify="center" width="100%">
          <ErrorPage />
        </Flex>
      </ErrorContainer>
    </Flex>
  );
}
